import React, { useCallback, useEffect, useRef, useState } from "react";
import { Modal } from "react-bootstrap";
import { Cropper } from "react-cropper";
import { prepareCropImageSrc, CROP_IMAGE_CORS_ERROR } from "./externalImportCropUtils";
import "cropperjs/dist/cropper.css";

const formatBytes = (bytes) => {
  const value = Number(bytes);
  if (!value || Number.isNaN(value)) return "—";
  if (value < 1024) return `${value} B`;
  if (value < 1024 * 1024) return `${(value / 1024).toFixed(0)} KB`;
  return `${(value / (1024 * 1024)).toFixed(2)} MB`;
};

const formatImageFormat = (fileType, fileName) => {
  if (fileType) {
    const subtype = fileType.split("/")[1]?.toLowerCase();
    if (subtype === "jpeg" || subtype === "jpg") return "JPEG";
    if (subtype === "png") return "PNG";
    if (subtype === "webp") return "WEBP";
    if (subtype === "gif") return "GIF";
    if (subtype) return subtype.toUpperCase();
  }
  const ext = fileName?.split(".").pop()?.toUpperCase();
  return ext || "—";
};

/**
 * Reusable cropping step. Receives a source image (object URL / data URL / URL),
 * lets the user crop freely, then returns a cropped File + preview URL.
 */
const CropperModal = ({
  show,
  image,
  fileName = "image.png",
  sourceFile = null,
  onCancel,
  onCropComplete,
  subtitle = "Adjust the crop area, then continue",
  applyLabel = "Crop & Continue",
  applying = false,
}) => {
  const cropperRef = useRef(null);
  const revokeRef = useRef(null);

  const [loadState, setLoadState] = useState("idle");
  const [loadError, setLoadError] = useState("");
  const [resolvedSrc, setResolvedSrc] = useState("");
  const [modalEntered, setModalEntered] = useState(false);
  const [cropperReady, setCropperReady] = useState(false);
  const [originalInfo, setOriginalInfo] = useState({
    width: null,
    height: null,
    size: "—",
    format: "—",
  });
  const [cropInfo, setCropInfo] = useState({
    width: null,
    height: null,
  });

  const imageUrl = String(image || "").trim();

  useEffect(() => {
    if (!show) {
      if (revokeRef.current) {
        revokeRef.current();
        revokeRef.current = null;
      }
      setLoadState("idle");
      setLoadError("");
      setResolvedSrc("");
      setModalEntered(false);
      setCropperReady(false);
      setOriginalInfo({ width: null, height: null, size: "—", format: "—" });
      setCropInfo({ width: null, height: null });
      return;
    }

    setOriginalInfo((prev) => ({
      ...prev,
      size: sourceFile?.size ? formatBytes(sourceFile.size) : "—",
      format: formatImageFormat(sourceFile?.type, fileName),
    }));

    if (!imageUrl) {
      setLoadError("Empty image URL");
      setLoadState("error");
      setResolvedSrc("");
      return;
    }

    let cancelled = false;

    const loadImage = async () => {
      setLoadState("loading");
      setLoadError("");
      setCropperReady(false);
      setResolvedSrc("");

      try {
        const prepared = await prepareCropImageSrc(imageUrl);
        if (cancelled) {
          prepared.revoke?.();
          return;
        }

        if (revokeRef.current) {
          revokeRef.current();
        }
        revokeRef.current = prepared.revoke;

        setResolvedSrc(prepared.src);
        setLoadState("ready");
      } catch (err) {
        if (!cancelled) {
          setLoadError(err?.message || CROP_IMAGE_CORS_ERROR);
          setLoadState("error");
          setResolvedSrc("");
        }
      }
    };

    loadImage();

    return () => {
      cancelled = true;
    };
  }, [show, imageUrl, sourceFile, fileName]);

  const updateCropInfo = useCallback(() => {
    const cropper = cropperRef.current?.cropper;
    if (!cropper) return;

    const data = cropper.getData(true);
    setCropInfo({
      width: Math.round(data.width),
      height: Math.round(data.height),
    });
  }, []);

  const handleCropEvent = useCallback(() => {
    updateCropInfo();
  }, [updateCropInfo]);

  const handleReady = useCallback(() => {
    const cropper = cropperRef.current?.cropper;
    if (!cropper) return;

    cropper.resize();

    const imageData = cropper.getImageData();
    setOriginalInfo((prev) => ({
      ...prev,
      width: Math.round(imageData.naturalWidth),
      height: Math.round(imageData.naturalHeight),
    }));

    updateCropInfo();
    setCropperReady(true);
  }, [updateCropInfo]);

  const getCropper = () => {
    if (!cropperReady) return null;
    return cropperRef.current?.cropper ?? null;
  };

  const handleZoomIn = () => {
    getCropper()?.zoom(0.1);
    updateCropInfo();
  };
  const handleZoomOut = () => {
    getCropper()?.zoom(-0.1);
    updateCropInfo();
  };
  const handleRotateLeft = () => {
    getCropper()?.rotate(-90);
    updateCropInfo();
  };
  const handleRotateRight = () => {
    getCropper()?.rotate(90);
    updateCropInfo();
  };
  const handleReset = () => {
    getCropper()?.reset();
    updateCropInfo();
  };

  const handleCrop = () => {
    const cropper = getCropper();
    if (!cropper) return;

    const canvas = cropper.getCroppedCanvas();
    if (!canvas) return;

    const cropData = cropper.getData(true);
    const outputWidth = Math.round(cropData.width);
    const outputHeight = Math.round(cropData.height);

    canvas.toBlob((blob) => {
      if (!blob) return;
      const safeName = fileName || "image.png";
      const croppedFile = new File([blob], safeName, {
        type: blob.type || "image/png",
      });
      const previewUrl = canvas.toDataURL(blob.type || "image/png");
      onCropComplete?.(croppedFile, previewUrl, {
        width: outputWidth,
        height: outputHeight,
        format: formatImageFormat(blob.type, safeName),
        estimatedSize: blob.size,
      });
    }, "image/png");
  };

  const formatDimension = (value) =>
    value != null && !Number.isNaN(value) ? `${value} px` : "—";

  const canUseCropper =
    loadState === "ready" && modalEntered && Boolean(resolvedSrc);
  const controlsEnabled = cropperReady && !applying;

  return (
    <Modal
      show={show}
      onHide={onCancel}
      onEntered={() => setModalEntered(true)}
      onExited={() => setModalEntered(false)}
      size="lg"
      centered
      backdrop="static"
      dialogClassName="xc-ml-cropper"
    >
      <Modal.Header closeButton>
        <div>
          <h5 className="xc-ml-header__title">
            <i className="ri-crop-line" aria-hidden="true" />
            Crop Image
          </h5>
          <p className="xc-ml-header__subtitle">{subtitle}</p>
        </div>
      </Modal.Header>

      <Modal.Body>
        <div className="card xc-ml-cropper__info-card mb-3">
          <div className="card-body py-3">
            <h6 className="xc-ml-cropper__info-title mb-3">Image Information</h6>
            <div className="row g-3">
              <div className="col-sm-6">
                <p className="xc-ml-cropper__info-row mb-0">
                  <span className="text-muted">Original Width</span>
                  <strong>{formatDimension(originalInfo.width)}</strong>
                </p>
              </div>
              <div className="col-sm-6">
                <p className="xc-ml-cropper__info-row mb-0">
                  <span className="text-muted">Original Height</span>
                  <strong>{formatDimension(originalInfo.height)}</strong>
                </p>
              </div>
              <div className="col-sm-6">
                <p className="xc-ml-cropper__info-row mb-0">
                  <span className="text-muted">Crop Width</span>
                  <strong>{formatDimension(cropInfo.width)}</strong>
                </p>
              </div>
              <div className="col-sm-6">
                <p className="xc-ml-cropper__info-row mb-0">
                  <span className="text-muted">Crop Height</span>
                  <strong>{formatDimension(cropInfo.height)}</strong>
                </p>
              </div>
              {sourceFile?.size ? (
                <div className="col-12">
                  <p className="xc-ml-cropper__info-meta mb-0">
                    <span className="text-muted">File Size</span>
                    <span>{originalInfo.size}</span>
                    <span className="xc-ml-cropper__format">{originalInfo.format}</span>
                  </p>
                </div>
              ) : null}
            </div>
          </div>
        </div>

        <div className="xc-ml-cropper__body">
          <div className="xc-ml-cropper__main">
            <div className="xc-ml-cropper__stage">
              {loadState === "loading" ? (
                <div className="xc-ml-cropper__state">
                  <div className="xc-ml-upload-state__spinner" />
                  <span>Loading image...</span>
                </div>
              ) : null}

              {loadState === "error" ? (
                <div className="xc-ml-cropper__state xc-ml-cropper__state--error">
                  <i className="ri-image-off-line" aria-hidden="true" />
                  <span>{loadError || CROP_IMAGE_CORS_ERROR}</span>
                </div>
              ) : null}

              {canUseCropper ? (
                <Cropper
                  key={resolvedSrc}
                  ref={cropperRef}
                  src={resolvedSrc}
                  style={{ width: "100%", height: "100%" }}
                  aspectRatio={NaN}
                  viewMode={1}
                  responsive
                  autoCropArea={1}
                  guides
                  checkOrientation={false}
                  background
                  dragMode="move"
                  ready={handleReady}
                  crop={handleCropEvent}
                  cropmove={handleCropEvent}
                  cropend={handleCropEvent}
                  zoom={handleCropEvent}
                />
              ) : null}
            </div>

            {loadState !== "error" ? (
              <div className="xc-ml-cropper__toolbar" role="toolbar" aria-label="Crop tools">
                <button
                  type="button"
                  className="xc-ml-cropper__tool"
                  onClick={handleZoomIn}
                  title="Zoom In"
                  disabled={!controlsEnabled}
                >
                  <i className="ri-zoom-in-line" aria-hidden="true" />
                  <span>Zoom In</span>
                </button>
                <button
                  type="button"
                  className="xc-ml-cropper__tool"
                  onClick={handleZoomOut}
                  title="Zoom Out"
                  disabled={!controlsEnabled}
                >
                  <i className="ri-zoom-out-line" aria-hidden="true" />
                  <span>Zoom Out</span>
                </button>
                <button
                  type="button"
                  className="xc-ml-cropper__tool"
                  onClick={handleRotateLeft}
                  title="Rotate Left"
                  disabled={!controlsEnabled}
                >
                  <i className="ri-anticlockwise-line" aria-hidden="true" />
                  <span>Rotate Left</span>
                </button>
                <button
                  type="button"
                  className="xc-ml-cropper__tool"
                  onClick={handleRotateRight}
                  title="Rotate Right"
                  disabled={!controlsEnabled}
                >
                  <i className="ri-clockwise-line" aria-hidden="true" />
                  <span>Rotate Right</span>
                </button>
                <button
                  type="button"
                  className="xc-ml-cropper__tool"
                  onClick={handleReset}
                  title="Reset"
                  disabled={!controlsEnabled}
                >
                  <i className="ri-restart-line" aria-hidden="true" />
                  <span>Reset</span>
                </button>
              </div>
            ) : null}
          </div>
        </div>

        {loadState !== "error" ? (
          <p className="xc-ml-cropper__hint">
            Drag to reposition • Use handles to resize the crop area
          </p>
        ) : null}
      </Modal.Body>

      <Modal.Footer>
        <button type="button" className="btn btn-light" onClick={onCancel} disabled={applying}>
          Cancel
        </button>
        <button
          type="button"
          className="btn btn-primary"
          onClick={handleCrop}
          disabled={!controlsEnabled}
        >
          {applying ? (
            <>
              <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true" />
              Applying...
            </>
          ) : (
            <>
              <i className="ri-check-line me-1" aria-hidden="true" />
              {applyLabel}
            </>
          )}
        </button>
      </Modal.Footer>
    </Modal>
  );
};

export default CropperModal;
